import { useEffect, useRef, useState } from "react";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Receipt, Camera, Upload, Sparkles, Loader2, Pencil, Trash2, Filter, Plus,
  FileSpreadsheet, FileText,
} from "lucide-react";
import DateRangeFilter from "@/components/DateRangeFilter";
import { exportToExcel, exportToPdf } from "@/lib/exportUtils";

const emptyGasto = {
  tienda: "", fecha: new Date().toISOString().slice(0, 10),
  descripcion: "", total: 0, metodo_pago: "", banco: "", caja_id: "", numero_factura: "", referencia: "",
  categoria: "", items: [],
};

export default function Gastos() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [modo, setModo] = useState("actual"); // actual | historial
  const [cierres, setCierres] = useState([]);
  const [cierreId, setCierreId] = useState("");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(emptyGasto);
  const [imgB64, setImgB64] = useState("");
  const [ocrLoading, setOcrLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [bancos, setBancos] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [metodos, setMetodos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [stats, setStats] = useState(null);
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCat, setNewCat] = useState("");
  const fileRef = useRef(null);

  const loadCats = async () => {
    try { const { data } = await api.get("/categorias-gasto"); setCategorias(data); } catch {}
  };
  const loadBancos = async () => {
    try {
      const b = await api.get("/bancos-activos");
      const m = await api.get("/metodos-pago-activos");
      const c = await api.get("/cajas").catch(() => ({ data: [] }));
      setBancos(b.data.filter((x) => (x.tipo || "banco") === "banco"));
      setMetodos(m.data);
      setCajas((c.data || []).filter((x) => x.activo !== false));
    } catch {}
  };
  const loadStats = async () => {
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      if (modo === "historial") {
        if (cierreId) params.cierre_id = cierreId;
        else params.historial = true;
      }
      const { data } = await api.get("/reports/gastos-por-categoria", { params });
      setStats(data);
    } catch {}
  };

  useEffect(() => { loadCats(); loadBancos(); api.get("/cierres-mes").then(({ data }) => setCierres(data || [])).catch(() => {}); }, []);
  useEffect(() => { loadStats(); }, [desde, hasta, rows.length, modo, cierreId]);

  const load = async () => {
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      if (modo === "historial") {
        if (cierreId) params.cierre_id = cierreId;
        else params.historial = true;
      }
      const { data } = await api.get("/gastos", { params });
      setRows(data.items);
      setTotal(data.total);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [desde, hasta, modo, cierreId]);

  const openNew = () => {
    setForm(emptyGasto);
    setImgB64("");
    setEditingId(null);
    setDialog(true);
  };
  const openEdit = async (g) => {
    try {
      const { data } = await api.get(`/gastos/${g.id}`);
      setForm({
        tienda: data.tienda || "",
        fecha: data.fecha || new Date().toISOString().slice(0, 10),
        descripcion: data.descripcion || "",
        total: data.total || 0,
        metodo_pago: data.metodo_pago || "",
        banco: data.banco || "",
        caja_id: data.caja_id || "",
        numero_factura: data.numero_factura || "",
        referencia: data.referencia || "",
        items: data.items || [],
      });
      setImgB64(data.imagen_base64 || "");
      setEditingId(g.id);
      setDialog(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Imagen muy grande (máx 6MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImgB64(reader.result);
    reader.readAsDataURL(file);
  };

  const runOcr = async () => {
    if (!imgB64) return toast.error("Sube una imagen primero");
    setOcrLoading(true);
    try {
      const { data } = await api.post("/gastos/ocr", { image_base64: imgB64 });
      setForm((prev) => ({
        ...prev,
        tienda: data.tienda || prev.tienda,
        fecha: data.fecha || prev.fecha,
        descripcion: data.descripcion || prev.descripcion,
        total: Number(data.total) || prev.total,
        metodo_pago: data.metodo_pago || prev.metodo_pago,
        banco: data.banco || prev.banco,
        referencia: data.referencia || prev.referencia,
        items: Array.isArray(data.items) ? data.items : prev.items,
      }));
      toast.success("Datos extraídos por IA. Revisa y guarda.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOcrLoading(false); }
  };

  const [saving, setSaving] = useState(false);

  const doSave = async () => {
    if (!form.tienda.trim()) { toast.error("Tienda requerida"); return false; }
    if (!form.fecha) { toast.error("Fecha requerida"); return false; }
    if (!form.total || form.total <= 0) { toast.error("Total > 0 requerido"); return false; }
    setSaving(true);
    try {
      const payload = { ...form, imagen_base64: imgB64 || "" };
      if (editingId) {
        const p = { ...form };
        await api.patch(`/gastos/${editingId}`, p);
      } else {
        await api.post("/gastos", payload);
      }
      toast.success("Guardado");
      await load();
      return true;
    } catch (e) { toast.error(formatApiError(e)); return false; }
    finally { setSaving(false); }
  };
  const save = async () => {
    if (saving) return;
    const ok = await doSave();
    if (ok) setDialog(false);
  };
  const saveAndAddAnother = async () => {
    if (saving) return;
    const ok = await doSave();
    if (!ok) return;
    // Limpiar formulario pero mantener fecha actual y modal abierto
    setForm({ ...emptyGasto, fecha: form.fecha || new Date().toISOString().slice(0, 10) });
    setImgB64("");
    setEditingId(null);
  };

  const [delMotivo, setDelMotivo] = useState({ id: null, motivo: "", open: false });
  const askDel = (id) => setDelMotivo({ id, motivo: "", open: true });
  const doDel = async () => {
    if (!delMotivo.motivo.trim()) return toast.error("Motivo obligatorio");
    try {
      await api.delete(`/gastos/${delMotivo.id}?motivo=${encodeURIComponent(delMotivo.motivo.trim())}`);
      setDelMotivo({ id: null, motivo: "", open: false });
      load();
      toast.success("Gasto anulado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="gastos-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Egresos</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <Receipt className="w-8 h-8 text-[#EF4444]" /> Facturas a Proveedores / Gastos
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">
            Sube la foto de la factura o recibo y la IA extrae automáticamente los datos.
          </p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <div className="flex gap-1">
            <button onClick={() => { setModo("actual"); setCierreId(""); }} className={`px-3 py-2 text-xs uppercase tracking-widest font-black rounded ${modo === "actual" ? "bg-black text-white" : "bg-zinc-100 text-zinc-600"}`} data-testid="gastos-tab-actual">Actual</button>
            <button onClick={() => setModo("historial")} className={`px-3 py-2 text-xs uppercase tracking-widest font-black rounded ${modo === "historial" ? "bg-black text-white" : "bg-zinc-100 text-zinc-600"}`} data-testid="gastos-tab-historial">Gastos Archivados</button>
          </div>
          {modo === "historial" && (
            <select value={cierreId} onChange={(e) => setCierreId(e.target.value)} className="h-10 border border-zinc-200 rounded-md px-3 text-sm" data-testid="gastos-hist-cierre">
              <option value="">Todos los cerrados</option>
              {cierres.map((c) => (
                <option key={c.id} value={c.id}>#{c.numero_cierre} · {c.mes_key}</option>
              ))}
            </select>
          )}
          <DateRangeFilter desde={desde} hasta={hasta} setDesde={setDesde} setHasta={setHasta} testidPrefix="gastos" />
          <Button variant="outline" onClick={() => {
            const cols = [
              { key: "fecha", label: "Fecha", type: "date", width: 14 },
              { key: "tienda", label: "Tienda / Proveedor", width: 22 },
              { key: "categoria", label: "Categoría", width: 16 },
              { key: "descripcion", label: "Descripción", width: 32 },
              { key: "metodo_pago", label: "Método", width: 14 },
              { key: "banco", label: "Banco", width: 16 },
              { key: "caja_id", label: "Caja", width: 14 },
              { key: "referencia", label: "Referencia", width: 16 },
              { key: "numero_factura", label: "N° Factura", width: 14 },
              { key: "total", label: "Monto", type: "money", width: 14 },
              { key: "created_by", label: "Registró", width: 22 },
            ];
            exportToExcel({ title: "Gastos", filters: { Desde: desde || "—", Hasta: hasta || "—", Registros: rows.length },
              columns: cols, rows, filename: `gastos_${desde || "all"}_${hasta || "all"}` });
          }} className="gap-2" data-testid="gastos-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </Button>
          <Button variant="outline" onClick={() => {
            const cols = [
              { key: "fecha", label: "Fecha", type: "date" },
              { key: "tienda", label: "Proveedor" },
              { key: "categoria", label: "Categoría" },
              { key: "descripcion", label: "Descripción" },
              { key: "metodo_pago", label: "Método" },
              { key: "banco", label: "Banco/Caja" },
              { key: "total", label: "Monto", type: "money" },
            ];
            exportToPdf({ title: "Gastos", filters: { Desde: desde || "—", Hasta: hasta || "—", Total: formatMoney(total) },
              columns: cols, rows, filename: `gastos_${desde || "all"}_${hasta || "all"}`, landscape: true });
          }} className="gap-2" data-testid="gastos-pdf">
            <FileText className="w-4 h-4" /> PDF
          </Button>
          {modo === "historial" && (
            <span className="text-[10px] uppercase tracking-widest font-black text-zinc-500 bg-amber-100 border border-amber-300 rounded px-2 py-1" data-testid="gastos-solo-lectura">Solo lectura</span>
          )}
          <Dialog open={dialog && modo === "actual"} onOpenChange={(v) => modo === "actual" && setDialog(v)}>
            <DialogTrigger asChild>
              {modo === "actual" ? (
                <Button className="gap-2 bg-[#EF4444] hover:bg-[#DC2626]" onClick={openNew} data-testid="gasto-new-btn">
                  <Plus className="w-4 h-4" /> Nuevo gasto
                </Button>
              ) : <span />}
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>{editingId ? "Editar gasto" : "Nuevo gasto — Sube la foto y extrae con IA"}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Foto de la factura</Label>
                  <div
                    className="mt-1 rounded-md border-2 border-dashed border-zinc-300 p-3 min-h-[240px] flex flex-col items-center justify-center bg-zinc-50 hover:border-blue-400 cursor-pointer"
                    onClick={() => fileRef.current?.click()}
                  >
                    <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => onFile(e.target.files?.[0])} data-testid="gasto-file-input" />
                    {imgB64 ? (
                      <img src={imgB64} alt="factura" className="max-h-56 max-w-full object-contain" />
                    ) : (
                      <div className="text-center text-zinc-500">
                        <Camera className="w-8 h-8 mx-auto mb-2" />
                        <div className="text-sm">Toca aquí para subir foto</div>
                        <div className="text-[11px] mt-1">JPEG, PNG o WEBP · máx 6MB</div>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={runOcr}
                    disabled={ocrLoading || !imgB64}
                    className="w-full mt-2 gap-2 bg-[#8B5CF6] hover:bg-[#7C3AED] disabled:opacity-40"
                    data-testid="gasto-ocr-btn"
                  >
                    {ocrLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {ocrLoading ? "Analizando con IA..." : "Extraer datos con IA (Gemini)"}
                  </Button>
                </div>
                <div className="space-y-2">
                  <div><Label>Tienda / Proveedor</Label><Input value={form.tienda} onChange={(e) => setForm({ ...form, tienda: e.target.value })} data-testid="gasto-tienda" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="gasto-fecha" /></div>
                    <div><Label>Total (Q)</Label><Input type="number" step="0.01" value={form.total} onChange={(e) => setForm({ ...form, total: Number(e.target.value) })} data-testid="gasto-total" /></div>
                  </div>
                  <div><Label>Descripción</Label><Textarea rows={2} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Método de pago</Label>
                      <Select value={form.metodo_pago || "none"} onValueChange={(v) => setForm({ ...form, metodo_pago: v === "none" ? "" : v })}>
                        <SelectTrigger data-testid="gasto-metodo"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">—</SelectItem>
                          {metodos.map((m) => <SelectItem key={m.id} value={m.nombre}>{m.nombre}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Cuenta (Banco o Caja)</Label>
                      <Select
                        value={form.caja_id ? `caja:${form.caja_id}` : (form.banco ? `banco:${form.banco}` : "none")}
                        onValueChange={(v) => {
                          if (v === "none") setForm({ ...form, banco: "", caja_id: "" });
                          else if (v.startsWith("banco:")) setForm({ ...form, banco: v.slice(6), caja_id: "" });
                          else if (v.startsWith("caja:")) setForm({ ...form, caja_id: v.slice(5), banco: "" });
                        }}
                      >
                        <SelectTrigger data-testid="gasto-cuenta"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">—</SelectItem>
                          {bancos.length > 0 && (
                            <div className="text-[9px] uppercase tracking-widest text-zinc-400 px-2 pt-1.5 pb-0.5 font-semibold">Bancos</div>
                          )}
                          {bancos.map((b) => <SelectItem key={`banco-${b.id}`} value={`banco:${b.nombre}`}>🏦 {b.nombre}</SelectItem>)}
                          {cajas.length > 0 && (
                            <div className="text-[9px] uppercase tracking-widest text-zinc-400 px-2 pt-1.5 pb-0.5 font-semibold">Cajas</div>
                          )}
                          {cajas.map((c) => <SelectItem key={`caja-${c.id}`} value={`caja:${c.id}`}>💰 {c.nombre}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>N° de factura <span className="text-[9px] text-zinc-400 font-normal">(auto si dejas vacío)</span></Label><Input value={form.numero_factura || ""} onChange={(e) => setForm({ ...form, numero_factura: e.target.value })} placeholder="Ej. G-1 (se asigna auto)" data-testid="gasto-numero-factura" /></div>
                    <div><Label>Referencia / Autorización</Label><Input value={form.referencia} onChange={(e) => setForm({ ...form, referencia: e.target.value })} data-testid="gasto-ref" /></div>
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <div>
                      <Label>Categoría <span className="text-[9px] text-zinc-400 font-normal">(si es "Cargo Expreso" carga saldo a Plataforma de Guías)</span></Label>
                      <div className="flex gap-1">
                        <Select value={form.categoria || "none"} onValueChange={(v) => {
                          if (v === "__new__") { setShowNewCat(true); return; }
                          setForm({ ...form, categoria: v === "none" ? "" : v });
                        }}>
                          <SelectTrigger data-testid="gasto-categoria"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">—</SelectItem>
                            <SelectItem value="Cargo Expreso">📦 Cargo Expreso</SelectItem>
                            {categorias.filter((c) => (c.nombre || "").toLowerCase() !== "cargo expreso").map((c) => <SelectItem key={c.id} value={c.nombre}>{c.nombre}</SelectItem>)}
                            <SelectItem value="__new__">+ Nueva categoría…</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  {showNewCat && (
                    <div className="flex gap-2 items-end p-2 bg-blue-50 rounded border border-blue-200">
                      <div className="flex-1"><Label>Nueva categoría</Label><Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Ej. Madera, Gasolina..." data-testid="gasto-new-cat-input" /></div>
                      <Button
                        size="sm"
                        onClick={async () => {
                          if (!newCat.trim()) return;
                          try {
                            await api.post("/categorias-gasto", { nombre: newCat.trim() });
                            await loadCats();
                            setForm({ ...form, categoria: newCat.trim() });
                            setNewCat("");
                            setShowNewCat(false);
                            toast.success("Categoría creada");
                          } catch (e) { toast.error(formatApiError(e)); }
                        }}
                        data-testid="gasto-new-cat-save"
                      >Crear</Button>
                      <Button size="sm" variant="ghost" onClick={() => { setShowNewCat(false); setNewCat(""); }}>×</Button>
                    </div>
                  )}
                  {form.items && form.items.length > 0 && (
                    <div className="text-xs bg-zinc-50 rounded p-2 border">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Items detectados</div>
                      <ul className="space-y-0.5">
                        {form.items.map((it, i) => (
                          <li key={i}>{it.cantidad}× {it.descripcion} — {formatMoney(it.subtotal || (it.cantidad * (it.precio_unitario || 0)))}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => saveAndAddAnother()} data-testid="gasto-save-and-add" disabled={saving}>
                  {saving ? "Guardando…" : "Guardar y agregar otro"}
                </Button>
                <Button onClick={save} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="gasto-save" disabled={saving}>
                  {saving ? "Guardando…" : "Guardar y cerrar"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Registros</div>
          <div className="font-manrope font-black text-3xl mt-1">{rows.length}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Total gastado</div>
          <div className="font-manrope font-black text-3xl mt-1 text-red-700">{formatMoney(total)}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Rango</div>
          <div className="text-sm mt-1">{desde || "inicio"} → {hasta || "hoy"}</div>
        </Card>
      </div>

      {/* Estadística por categoría */}
      {stats && stats.items.length > 0 && (
        <Card className="p-5 border-zinc-200 rounded-md" data-testid="stats-categorias">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">📊 Gastos por Categoría</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {stats.items.map((s) => (
              <div key={s.categoria} className="border border-zinc-200 rounded-md p-3 bg-white" data-testid={`stats-cat-${s.categoria}`}>
                <div className="flex justify-between items-center mb-1">
                  <span className="font-semibold text-sm">{s.categoria}</span>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-blue-700">{s.porcentaje}%</span>
                </div>
                <div className="font-manrope font-black text-xl text-red-700">{formatMoney(s.total)}</div>
                <div className="mt-1.5 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full bg-red-500" style={{ width: `${s.porcentaje}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Fecha</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Tienda / Proveedor</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Descripción</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold text-right">Total</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">N° factura</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Método</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Banco / Caja</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Categoría</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Referencia</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.map((g) => (
                <tr key={g.id} className="hover:bg-zinc-50" data-testid={`gasto-row-${g.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">{g.fecha}</td>
                  <td className="px-4 py-3 font-semibold">{g.tienda}</td>
                  <td className="px-4 py-3 text-xs text-zinc-700 max-w-md truncate" title={g.descripcion}>{g.descripcion}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-red-700">{formatMoney(g.total)}</td>
                  <td className="px-4 py-3 text-xs font-mono">{g.numero_factura || "—"}</td>
                  <td className="px-4 py-3 text-xs capitalize">{g.metodo_pago || "—"}</td>
                  <td className="px-4 py-3 text-xs">{g.caja_id ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-[10px] font-semibold">💰 {cajas.find(c => c.id === g.caja_id)?.nombre || "Caja"}</span> : (g.banco || "—")}</td>
                  <td className="px-4 py-3 text-xs">{g.categoria ? <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-[10px] font-semibold">{g.categoria}</span> : "—"}</td>
                  <td className="px-4 py-3 text-xs font-mono">{g.referencia || "—"}</td>
                  <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                    {modo === "actual" ? (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(g)}><Pencil className="w-3 h-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => askDel(g.id)} data-testid={`gasto-del-${g.id}`}><Trash2 className="w-3 h-3 text-red-600" /></Button>
                      </>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">Archivado</span>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={10} className="text-center text-zinc-400 py-10"><Receipt className="w-8 h-8 mx-auto mb-2 text-zinc-300" />Sin gastos registrados</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={delMotivo.open} onOpenChange={(v) => setDelMotivo({ ...delMotivo, open: v })}>
        <DialogContent data-testid="dialog-motivo-gasto">
          <DialogHeader>
            <DialogTitle>Anular gasto</DialogTitle>
          </DialogHeader>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Motivo (obligatorio)</Label>
            <Textarea
              rows={3}
              value={delMotivo.motivo}
              onChange={(e) => setDelMotivo({ ...delMotivo, motivo: e.target.value })}
              placeholder="Motivo de la anulación..."
              autoFocus
              data-testid="input-motivo-gasto"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelMotivo({ id: null, motivo: "", open: false })}>Cancelar</Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={doDel}
              disabled={!delMotivo.motivo.trim()}
              data-testid="confirm-motivo-gasto"
            >
              Confirmar anulación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
